import Layout from '@/src/layout/EditorPageLayout/EditorPageLayout';
import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import { getI18nFromGeneral } from '@/src/tools/tools';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { get } from 'lodash';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Form } from 'src/components/Antd';
import { EnumFieldType as Name } from 'src/components/FieldModal/config';
import RouteBlock from 'src/constrains/RouterBlock/RouterBlock';
import { useI18n } from 'src/hooks/useRouterChange';
import { SalesChannelMode } from 'src/types/common/SalesChannel';
import { useSalesChannelMode } from 'src/constrains/SalesChannelHOC/useSalesChannel';
import { RegistrationErrorContext } from 'src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelRegistration/ErrorContext';
import {
  getCustomerDBs,
  getRegistration,
  postRegistration,
} from 'src/request/salesChannel/setting/registration';
import { useComponentMemo } from 'src/tools/customHooks';
import { timeoutScroll } from 'src/tools/helper';
import { msg } from 'src/tools/message';
import { i18nGet } from 'src/tools/tools';
import TopLeft from './Components/TopLeft';
import TopRight from './Components/TopRight';
import _CustomerDatabase from './CustomerDatabase';
import _Login2FA from './CustomerLogin2FA';
import _Method from './CustomerRegistrationMethod';
import Provider from './ErrorContext';
import _PasswordFormat from './PasswordFormat';
import _PublicAccess from './PublicAccess';
import _AcctionDeletion from './CustomerAccountDeletion';
import _RegistrationInformation, {
  EFormSetting,
} from './RegistrationInformation';

import Tc from './TermsAndConditions';
import { convertDataFromServer as cD } from './tools';

const permissions = (uuid: string) => {
  const view_registration_settings = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelRegistration,
        operation: EnumOperation.view_registration_settings,
        uuid,
      }),
    ],
  });
  const edit_registration_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelRegistration,
        operation: EnumOperation.edit_registration_setting,
        uuid,
      }),
    ],
  });
  return {
    view_registration_settings,
    edit_registration_setting,
  };
};

const Registration = (props) => {
  const i18n = useI18n();
  const mode = useSalesChannelMode();
  const getI18n = (str) => i18nGet(i18n, str);
  const {
    setDbEmptyError,
    setLandingImageError,
    setRegEmptyError,
    setTcError,
    setMethodError,
  } = React.useContext(RegistrationErrorContext);
  const { uuid: salesChannelUuid }: any = useParams();
  const { view_registration_settings, edit_registration_setting } =
    permissions(salesChannelUuid);
  const [form] = Form.useForm();
  const [hasModified, setHasModified] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [fields, setFields] = React.useState([]);
  //dbs
  const [dbs, setDbs] = React.useState([]);
  //db
  const [customerDBMetaInfo, setCustomerDBMetaInfo] = React.useState(null);
  const supportedCustomerFields = (
    get(customerDBMetaInfo, 'supportedCustomerFields', []) || []
  ).map((i) => i.field);

  const supportThirdPartyLogin = get(
    customerDBMetaInfo,
    'supportThirdPartyLogin',
    false,
  );
  // console.log('supportThirdPartyLogin', supportThirdPartyLogin);
  //methods
  const [method, setMethod] = React.useState({
    enableMobileRegistration: false,
    enableEmailRegistration: false,
    enableThirdPartyLoginRegistration: false,
  });
  //public access
  const [publicAccess, setPublicAccess] = React.useState({
    backgroundImage: null,
  });
  //Registration
  const [registration, setRegistration] = React.useState({
    customerInfoFormSetting: EFormSetting.NON_SKIPPABLE,
    customerInfoFields: [],
  });
  //tc
  const [termConditions, setTermAndConditions] = React.useState([]);
  const [tcList, setTcList] = React.useState([]);
  //rest
  const restData = React.useRef<any>({});
  //
  const getDbs = () => {
    return getCustomerDBs();
  };
  const getData = () => {
    return getRegistration(salesChannelUuid);
  };
  const assData = (res) => {
    return cD(res);
  };
  //
  const convertDataFromServer = (data) => {
    const {
      customerDBMetaInfo,
      method,
      publicAccess,
      registration,
      termConditions,
      termConditionList,
      predefinedCustomerFields,
      passwordFormat,
      login2FA,
      enableLoginRemarks,
      loginRemarks,
      visibilityPolicy,
      enableOnlyVisibleAfterLogin,
      allowAccessByCode,
      accessCodes,
      accountDeletable,
      ...rest
    }: any = assData(data);
    restData.current = rest;

    const _predefinedCustomerFields = (predefinedCustomerFields || []).map(
      (i) => i.field,
    );
    setFields(_predefinedCustomerFields);
    setCustomerDBMetaInfo(customerDBMetaInfo);
    setMethod(method);
    setPublicAccess(publicAccess);
    setLoaded(true);
    setRegistration(registration);
    setTcList(termConditionList);
    setTermAndConditions(termConditions);
    form.setFieldsValue({
      passwordFormat: passwordFormat || {},
      login2FA,
      enableLoginRemarks,
      loginRemarks,
      visibilityPolicy,
      enableOnlyVisibleAfterLogin,
      allowAccessByCode,
      accessCodes,
      accountDeletable,
    });
    setHasModified(false);
  };

  React.useEffect(() => {
    if (view_registration_settings) {
      Promise.all([getDbs(), getData()]).then((res) => {
        const [dbList, data]: any = res;
        setDbs(dbList || []);
        convertDataFromServer(data);
        setLoaded(true);
      });
    } else {
      setLoaded(true);
    }
  }, []);

  const CustomerDatabase = useComponentMemo(
    <_CustomerDatabase
      data={customerDBMetaInfo}
      onChange={setCustomerDBMetaInfo}
      setHasModified={setHasModified}
      list={dbs}
      disableEdit={!edit_registration_setting}
    />,
    [dbs, customerDBMetaInfo],
  );

  const Method = useComponentMemo(
    <_Method
      data={method}
      onChange={setMethod}
      setHasModified={setHasModified}
      supportThirdPartyLogin={supportThirdPartyLogin}
      disableEdit={!edit_registration_setting}
    />,
    [supportThirdPartyLogin, method],
  );

  const PublicAccess = useComponentMemo(
    <_PublicAccess
      data={publicAccess}
      onChange={setPublicAccess}
      setHasModified={setHasModified}
      disableEdit={!edit_registration_setting}
      form={form}
    />,
    [publicAccess],
  );

  const RegistrationInformation = useComponentMemo(
    <_RegistrationInformation
      data={registration}
      onChange={setRegistration}
      setHasModified={setHasModified}
      fields={
        supportedCustomerFields.length > 0 ? supportedCustomerFields : fields
      }
      disableEdit={!edit_registration_setting}
    />,
    [supportedCustomerFields, registration, fields],
  );

  const TNC = useComponentMemo(
    <Tc
      data={termConditions}
      onChange={setTermAndConditions}
      tcs={tcList}
      setHasModified={setHasModified}
      disableEdit={!edit_registration_setting}
    />,
    [termConditions, tcList],
  );

  const PasswordFormat = useComponentMemo(
    <_PasswordFormat
      form={form}
      setHasModified={setHasModified}
      disableEdit={!edit_registration_setting}
    />,
    [form],
  );

  const Login2FA = useComponentMemo(
    <_Login2FA
      setHasModified={setHasModified}
      disableEdit={!edit_registration_setting}
    />,
    [],
  );

  const AccionDeletion = useComponentMemo(
    <_AcctionDeletion
      setHasModified={setHasModified}
      disableEdit={!edit_registration_setting}
    />,
    [],
  );

  const ele = view_registration_settings
    ? mode === SalesChannelMode.ECOMMERCE
      ? [
          { child: CustomerDatabase },
          { child: Method },
          { child: PublicAccess },
          { child: RegistrationInformation },
          { child: TNC },
          { child: PasswordFormat },
          { child: Login2FA },
          { child: AccionDeletion },
        ]
      : [{ child: CustomerDatabase }]
    : [];

  const onFinishFailed = ({ values, errorFields, outOfDate }) => {
    const pre = 'salesChannel-registration_';

    if (errorFields[0]) {
      const first = (errorFields[0] || {}).name || [];
      const suf = first.join('_');
      const id = pre + suf;
      timeoutScroll(id);
    }
  };

  const handleOnSubmit = (values) => {
    if (
      !get(customerDBMetaInfo, 'uuid') //!(customerDBMetaInfo || {}).uuid
    ) {
      setDbEmptyError(true);
      timeoutScroll('salesChannelRegistrationDbSelection');
      return;
    }
    if (mode === SalesChannelMode.ECOMMERCE) {
      if (
        !(
          get(method, 'enableMobileRegistration') ||
          get(method, 'enableEmailRegistration') ||
          get(method, 'enableThirdPartyLoginRegistration')
        ) //!(customerDBMetaInfo || {}).uuid
      ) {
        setMethodError(true);
        timeoutScroll('salesChannelRegistrationMethod');
        return;
      }
      if (
        values.enableOnlyVisibleAfterLogin &&
        !get(publicAccess, 'backgroundImage.uuid')
      ) {
        setLandingImageError(true);
        timeoutScroll('SalesChannelRegistrationLandingImage');
        return;
      }
      if ((get(registration, 'customerInfoFields', []) || []).length < 1) {
        setRegEmptyError(true);
        timeoutScroll('SalesChannelRegistrationInformationTable');
        return;
      }
      if (termConditions.length < 1) {
        setTcError(true);
        timeoutScroll('SalesChannelRegistrationTCTable');
        return;
      }
    }

    const _customerInfoFields = get(registration, 'customerInfoFields') || [];
    const customerInfoFields = _customerInfoFields.map((c) => {
      const fieldType = get(c, 'field.fieldType');

      const isEmail = Name.Email === fieldType;
      return isEmail ? { ...c, otpRequired: true } : c;
    });
    const res = {
      ...(restData.current || {}),
      salesChannelUuid,
      customerDBMetaInfo,
      //
      ...(method || {}),
      //
      ...(publicAccess || {}),
      enableLoginRemarks: values.enableLoginRemarks,
      loginRemarks: values.loginRemarks,
      visibilityPolicy: values.visibilityPolicy,
      enableOnlyVisibleAfterLogin: values.enableOnlyVisibleAfterLogin,
      accessCodes: values.accessCodes,
      allowAccessByCode: values.allowAccessByCode,

      //
      ...(registration || {}),
      customerInfoFields,
      //
      termConditions,
      //
      passwordFormat: values.passwordFormat,
      //
      login2FA: values.login2FA,

      accountDeletable: values.accountDeletable,
    };
    postRegistration(res).then((re) => {
      convertDataFromServer(re);
      msg.success(
        getI18n('salesChannelRegistration.index.registrationhasbeenupdated'),
      );
    });
  };

  return (
    <React.Fragment>
      {/* <button
        onClick={() => {
          setFields(fields2);
        }}>
        切换
      </button> */}
      <RouteBlock
        onConfirm={(f) => f()}
        okText={getI18nFromGeneral('general.commonHeader.discard_text')}
        active={hasModified}
        title={getI18nFromGeneral('general.commonHeader.discard_header')}
        content={getI18nFromGeneral('general.commonHeader.leave_content')}
      />
      <Form
        onFinishFailed={onFinishFailed}
        form={form}
        name='salesChannel-registration'
        onFinish={handleOnSubmit}>
        {loaded ? (
          <Layout
            topLeft={<TopLeft />}
            topRight={
              <TopRight
                hasModified={hasModified}
                disableEdit={!edit_registration_setting}
                onSubmit={() => {
                  form.submit();
                }}
              />
            }
            elements={ele}
          />
        ) : null}
      </Form>
    </React.Fragment>
  );
};

const _R = (props) => {
  return (
    <Provider>
      <Registration {...props} />
    </Provider>
  );
};

export default _R;
